import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { APP_VERSION, ANDROID_VERSION_CODE } from '@/config/version';

const PWAUpdateNotification = () => {
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);
  const [isNativeUpdate, setIsNativeUpdate] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [serverVersion, setServerVersion] = useState(null);
  const checkInterval = useRef(null);

  useEffect(() => {
    const isNative = window.Capacitor?.isNativePlatform?.() || window.Capacitor?.isNative || navigator.userAgent.includes('Capacitor');

    // En APK Capacitor, /version.json resuelve al bundle local (siempre la versión instalada).
    // Hay que pedir la versión al servidor de producción para detectar actualizaciones.
    const VERSION_URL = isNative
      ? `https://pgastos.capacitatu.com/version.json?t=${Date.now()}`
      : `/version.json?t=${Date.now()}`;

    // Función de chequeo universal (Web y Nativa)
    const checkForUpdates = async () => {
      try {
        const response = await fetch(VERSION_URL);
        if (!response.ok) return;
        const data = await response.json();

        // Verificar si el usuario ya descartó esta versión específica
        const dismissedVersion = localStorage.getItem('dismissed_update_version');
        if (dismissedVersion === data.version) return;

        setServerVersion(data.version);

        let hasUpdate = false;
        if (isNative) {
          if (data.versionCode > ANDROID_VERSION_CODE) {
            setIsNativeUpdate(true);
            hasUpdate = true;
          }
        } else {
          if (data.version !== APP_VERSION) {
            setIsNativeUpdate(false);
            hasUpdate = true;
          }
        }

        if (hasUpdate) {
          setShowUpdateNotification(true);
        } else {
          setShowUpdateNotification(false);
        }
      } catch (error) {
        console.warn('⚠️ Error al verificar actualizaciones:', error);
      }
    };

    // 1. Chequeo inicial inmediato
    checkForUpdates();

    // 2. Chequeo periódico cada 60 segundos
    checkInterval.current = setInterval(checkForUpdates, 60000);

    // 3. Mantener lógica de Service Worker como respaldo para el botón "Actualizar"
    if ('serviceWorker' in navigator && !isNative) {
      navigator.serviceWorker.getRegistration().then(reg => {
        if (reg) {
          if (reg.waiting) setWaitingWorker(reg.waiting);
          
          reg.addEventListener('updatefound', () => {
            const newWorker = reg.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  setWaitingWorker(newWorker);
                }
              });
            }
          });
        }
      });
    }

    return () => {
      if (checkInterval.current) clearInterval(checkInterval.current);
    };
  }, []);

  const handleUpdate = () => {
    if (serverVersion) {
      localStorage.setItem('dismissed_update_version', serverVersion);
    }
    setShowUpdateNotification(false);

    if (isNativeUpdate) {
      window.open('https://play.google.com/store/apps/details?id=com.vjrdev.pgastos', '_system');
      return;
    }

    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    }
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(regs => {
        regs.forEach(reg => reg.update());
      });
    }

    setTimeout(() => window.location.reload(), 300);
  };

  const handleDismiss = () => {
    if (serverVersion) {
      localStorage.setItem('dismissed_update_version', serverVersion);
    }
    setShowUpdateNotification(false);
  };

  if (!showUpdateNotification) return null;

  return (
    <div 
      className="fixed right-4 left-4 sm:left-auto sm:max-w-sm z-[999] animate-in slide-in-from-top-4 duration-300"
      style={{ top: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}
    >
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-2xl p-4 max-w-sm relative border border-blue-400">
        <button onClick={handleDismiss} className="absolute top-2 right-2 p-1 hover:bg-white/20 rounded-lg transition-colors">
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <RefreshCw className="w-5 h-5 animate-spin" />
          </div>
          <div className="flex-1 pr-6">
            <h3 className="font-bold text-sm mb-1">
              {isNativeUpdate ? '¡Nueva versión en Store!' : '¡Versión Nueva!'}
            </h3>
            <p className="text-xs text-blue-100">
              {isNativeUpdate 
                ? 'Hay mejoras listas en la Google Play Store.' 
                : 'Hay mejoras listas para tu app.'
              }
            </p>
          </div>
        </div>
        <button 
          onClick={handleUpdate}
          className="w-full py-2 bg-white text-blue-600 rounded-lg font-bold text-sm hover:bg-blue-50 shadow-md flex items-center justify-center gap-2 transition-all active:scale-95"
        >
          <RefreshCw className="w-4 h-4" />
          {isNativeUpdate ? 'Ir a la tienda' : 'Actualizar ahora'}
        </button>
      </div>
    </div>
  );
};

export default PWAUpdateNotification;
